import { useEffect, useState, useCallback, useRef } from "react";
import { Box, Flex, Heading, Text, Button, Input, Stack, HStack, Badge, Icon, Spinner, Textarea } from "@chakra-ui/react";
import { Upload, Search, FileText, Image as ImageIcon, FileSpreadsheet, Presentation, File, ExternalLink, Trash2, BookOpen, RefreshCw } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { cachePut, cacheGet, safeQuery, safeAll } from "@/lib/offline";
import { Modal } from "@/components/Modal";
import { ConfirmModal } from "@/components/ConfirmModal";
import { RequestAckButton, RequestAckModal } from "@/components/RequestAckModal";
import { inputStyle } from "@/pages/auth/AuthShell";
import { colors } from "@/theme/colors";

export const CATEGORIES = ["Manuals", "Procedures", "Training Certifications", "Safety Bulletins"];
const MAX_MB = 25;
const ALLOWED = {
  "application/pdf": "PDF",
  "application/msword": "Word",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word",
  "application/vnd.ms-excel": "Excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "Excel",
  "application/vnd.ms-powerpoint": "PowerPoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PowerPoint",
  "image/jpeg": "Image", "image/png": "Image", "image/webp": "Image",
};

const fileIcon = (type) => {
  if (type?.startsWith("image/")) return ImageIcon;
  if (type === "application/pdf") return FileText;
  if (type?.includes("spreadsheet") || type?.includes("excel")) return FileSpreadsheet;
  if (type?.includes("presentation") || type?.includes("powerpoint")) return Presentation;
  if (type?.includes("word")) return FileText;
  return File;
};

const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

function UploadModal({ companyId, isGlobal, defaultCategory, existing, onClose, onDone, flash }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(CATEGORIES.includes(defaultCategory) ? defaultCategory : CATEGORIES[0]);
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [err, setErr] = useState(null);
  const [dupWarn, setDupWarn] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const pick = (e) => {
    setErr(null);
    setDupWarn(null);
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!ALLOWED[f.type]) {
      setErr(`"${f.name}" isn't a supported file. Allowed: PDF, Word, Excel, PowerPoint, JPG, PNG, WEBP.`);
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      setErr(`"${f.name}" is ${(f.size / 1048576).toFixed(1)} MB — files must be under ${MAX_MB} MB.`);
      return;
    }
    setFile(f);
    if (!title.trim()) setTitle(f.name.replace(/\.[^.]+$/, ""));
  };

  const submit = async () => {
    if (!title.trim() || !file) { setErr(!file ? "Choose a file to upload." : "Give the resource a title."); return; }
    // Duplicate guard: same file name or same title already in this company's library.
    if (!dupWarn) {
      const dup = (existing || []).find(
        (r) => r.file_name === file.name || r.title.trim().toLowerCase() === title.trim().toLowerCase()
      );
      if (dup) {
        setDupWarn(`“${dup.title}” (${dup.file_name}) is already in ${dup.category}. Upload this file again anyway?`);
        return;
      }
    }
    setBusy(true);
    setErr(null);
    try {
      if (isGlobal) {
        // Hakina GLOBAL resource: goes through the backend for server-side
        // type/size enforcement and Hakina-owned storage ('global/' folder).
        const { data: sess } = await supabase.auth.getSession();
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", title.trim());
        fd.append("category", category);
        fd.append("description", description.trim());
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/resources/global`, {
          method: "POST",
          headers: { Authorization: `Bearer ${sess?.session?.access_token}` },
          body: fd,
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.detail || "Upload failed");
        flash("success", `“${title.trim()}” published to all companies (${category}).`);
        onDone(category);
        return;
      }
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `${companyId}/${crypto.randomUUID()}-${safeName}`;
      const { error: upErr } = await supabase.storage.from("resources").upload(path, file, { contentType: file.type });
      if (upErr) throw upErr;
      const { error: insErr } = await supabase.from("resources").insert({
        company_id: companyId, category, title: title.trim(), description: description.trim(),
        file_path: path, file_name: file.name, file_type: file.type, file_size: file.size,
        uploaded_by: (await supabase.auth.getUser()).data.user.id,
      });
      if (insErr) {
        await supabase.storage.from("resources").remove([path]);
        throw insErr;
      }
      flash("success", `“${title.trim()}” uploaded to ${category}.`);
      onDone(category);
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={isGlobal ? "Publish global resource (all companies)" : "Upload resource"} testId="upload-modal">
      <Stack gap="4">
        <Box>
          <Text fontSize="sm" fontWeight="500" mb="1.5">File</Text>
          <input ref={fileRef} type="file" hidden onChange={pick} data-testid="upload-file-input"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,image/jpeg,image/png,image/webp" />
          <Button w="full" h="52px" variant="outline" borderStyle="dashed"
            color={file ? colors.tealHover : colors.textSecondary} onClick={() => fileRef.current?.click()}
            data-testid="upload-choose-file-btn">
            <Upload size={16} /> {file ? `${file.name} (${fmtSize(file.size)})` : "Choose a file"}
          </Button>
          <Text fontSize="xs" color={colors.textSecondary} mt="1">PDF, Word, Excel, PowerPoint or image · max {MAX_MB} MB</Text>
        </Box>
        <Box>
          <Text fontSize="sm" fontWeight="500" mb="1.5">Title</Text>
          <Input {...inputStyle} value={title} onChange={(e) => { setTitle(e.target.value); setDupWarn(null); }} data-testid="upload-title-input" />
        </Box>
        <Box>
          <Text fontSize="sm" fontWeight="500" mb="1.5">Category</Text>
          <Box as="select" w="full" px="3" py="2.5" borderRadius="8px" border="1px solid" borderColor={colors.border}
            bg={colors.white} fontSize="sm" value={category} onChange={(e) => setCategory(e.target.value)}
            data-testid="upload-category-select">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </Box>
        </Box>
        <Box>
          <Text fontSize="sm" fontWeight="500" mb="1.5">Description (optional)</Text>
          <Textarea {...inputStyle} height="auto" minH="70px" py="2" value={description}
            onChange={(e) => setDescription(e.target.value)} data-testid="upload-description-input" />
        </Box>
        {err && <Text fontSize="sm" color={colors.criticalText} data-testid="upload-error">{err}</Text>}
        {dupWarn && (
          <Box px="3.5" py="2.5" borderRadius="10px" bg="#FBEEDA" color="#8A5106" fontSize="sm" data-testid="duplicate-warning">
            {dupWarn}
          </Box>
        )}
        <Button bg={dupWarn ? "#8A5106" : colors.teal} _hover={{ bg: dupWarn ? "#6E4005" : colors.tealHover }}
          onClick={submit} loading={busy} data-testid="upload-submit-btn">
          {dupWarn ? "Yes, upload duplicate" : "Upload resource"}
        </Button>
        {dupWarn && (
          <Button variant="outline" onClick={onClose} data-testid="cancel-duplicate-btn">
            Cancel — keep the existing one
          </Button>
        )}
      </Stack>
    </Modal>
  );
}

export default function Resources() {
  const { user, role, activeCompanyId, companyName, isSuperAdmin } = useAuth();
  const canManage = role === "org_admin";
  const canRequest = ["org_admin", "supervisor"].includes(role);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("All");
  const [msg, setMsg] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [opening, setOpening] = useState(null);
  const [reqAckFor, setReqAckFor] = useState(null); // resource pre-locked in the request dialog
  const [delTarget, setDelTarget] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const [updFor, setUpdFor] = useState(null); // resource awaiting a new-version file pick
  const [updTarget, setUpdTarget] = useState(null); // { resource, file } pending confirm
  const [updBusy, setUpdBusy] = useState(false);
  const updRef = useRef(null);

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4500); };

  const load = useCallback(async () => {
    // Global Hakina resources (company_id NULL) are shown to every entitled
    // company alongside their own; the Super-Admin manages the global set.
    let q = supabase.from("resources").select("*").order("created_at", { ascending: false });
    q = activeCompanyId ? q.or(`company_id.is.null,company_id.eq.${activeCompanyId}`) : q.is("company_id", null);
    const { data, error } = await safeQuery(q);
    if (error) {
      // Offline: serve the last-known-good library instead of an empty state.
      setItems((cacheGet(user.id, "resources") || {}).items || []);
    } else {
      setItems(data || []);
      cachePut(user.id, "resources", { items: data || [] });
    }
    setLoading(false);
  }, [activeCompanyId, user.id]);

  useEffect(() => { load(); }, [load]);

  // Time-limited signed URL (5 min) — bucket is private, no public URLs exist.
  const view = async (r) => {
    setOpening(r.id);
    const { data, error } = await supabase.storage.from("resources").createSignedUrl(r.file_path, 300);
    setOpening(null);
    if (error || !data?.signedUrl) {
      flash("error", navigator.onLine
        ? "Could not open this file. Please try again."
        : "You're offline — documents can be opened once you're back online.");
      return;
    }
    window.open(data.signedUrl, "_blank", "noopener");
  };

  const remove = async () => {
    const r = delTarget;
    setDelBusy(true);
    await supabase.storage.from("resources").remove([r.file_path]);
    const { error } = await supabase.from("resources").delete().eq("id", r.id);
    setDelBusy(false);
    setDelTarget(null);
    if (error) flash("error", error.message);
    else { flash("success", `“${r.title}” deleted.`); load(); }
  };

  // New-version flow: pick file -> confirm supersede -> backend bumps version,
  // supersedes open acknowledgements and re-requests against the new version.
  const pickNewVersion = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !updFor) return;
    if (!ALLOWED[file.type]) { flash("error", `"${file.name}" isn't a supported file type.`); setUpdFor(null); return; }
    if (file.size > MAX_MB * 1024 * 1024) { flash("error", `Files must be under ${MAX_MB} MB.`); setUpdFor(null); return; }
    setUpdTarget({ resource: updFor, file });
    setUpdFor(null);
  };

  const doNewVersion = async () => {
    setUpdBusy(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const fd = new FormData();
      fd.append("file", updTarget.file);
      fd.append("redirect_to", window.location.origin);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/resources/${updTarget.resource.id}/new-version`, {
        method: "POST", headers: { Authorization: `Bearer ${sess?.session?.access_token}` }, body: fd,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof json.detail === "string" ? json.detail : "Update failed");
      flash("success", `“${updTarget.resource.title}” is now v${json.version} — ${json.superseded} open acknowledgement${json.superseded === 1 ? "" : "s"} superseded, ${json.rerequested} fresh request${json.rerequested === 1 ? "" : "s"} sent.`);
      setUpdTarget(null);
      load();
    } catch (e) {
      setUpdTarget(null);
      flash("error", e.message);
    } finally { setUpdBusy(false); }
  };

  const filtered = items.filter((r) =>
    (cat === "All" || r.category === cat) &&
    (search.trim() === "" || `${r.title} ${r.description} ${r.file_name}`.toLowerCase().includes(search.trim().toLowerCase()))
  );

  return (
    <Box data-testid="resources-page" maxW="720px">
      <Flex justify="space-between" align="flex-start" gap="3" flexWrap="wrap">
        <Box>
          <Heading fontFamily="heading" fontWeight="700" fontSize={{ base: "2xl", md: "3xl" }} color={colors.textPrimary}>Resources</Heading>
          <Text color={colors.textSecondary} mb="4">
            {isSuperAdmin
              ? "Hakina's global library — everything here is published to every client company."
              : `${companyName}'s safety library — manuals, procedures, certifications and bulletins.`}
          </Text>
        </Box>
        <HStack gap="2" flexWrap="wrap">
          {canRequest && <RequestAckButton />}
          {(canManage || isSuperAdmin) && (
            <Button variant={canRequest ? "outline" : "solid"}
              onClick={() => setUploading(true)} data-testid="upload-resource-btn">
              <Upload size={15} /> {isSuperAdmin ? "+ Resources" : "Upload resource"}
            </Button>
          )}
        </HStack>
      </Flex>

      {msg && (
        <Box mb="4" px="4" py="2.5" borderRadius="10px" fontSize="sm"
          bg={msg.type === "error" ? colors.criticalBg : colors.resolvedBg}
          color={msg.type === "error" ? colors.criticalText : colors.resolvedText}
          data-testid="resources-banner">{msg.text}</Box>
      )}

      {/* Search + category chips (phone-friendly) */}
      <Box position="relative" mb="3">
        <Icon as={Search} boxSize="4" color={colors.textSecondary} position="absolute" left="3" top="50%" transform="translateY(-50%)" />
        <Input {...inputStyle} pl="9" placeholder="Search resources…" value={search}
          onChange={(e) => setSearch(e.target.value)} data-testid="resource-search-input" />
      </Box>
      <Flex gap="2" mb="5" overflowX="auto" pb="1" css={{ scrollbarWidth: "none" }}>
        {["All", ...CATEGORIES].map((c) => {
          const active = cat === c;
          return (
            <Button key={c} size="xs" flexShrink={0} onClick={() => setCat(c)}
              data-testid={`cat-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
              bg={active ? colors.teal : colors.white} color={active ? "white" : colors.textPrimary} borderColor={active ? colors.teal : colors.border} borderRadius="full" px="3.5"
              _hover={{ bg: active ? colors.tealHover : colors.cream }}>
              {c}
            </Button>
          );
        })}
      </Flex>

      {loading ? (
        <Flex minH="30vh" align="center" justify="center"><Spinner color={colors.teal} borderWidth="3px" /></Flex>
      ) : filtered.length === 0 ? (
        <Flex direction="column" align="center" gap="2" py="12" borderRadius="12px" border="1px dashed" borderColor={colors.border} bg={colors.white} data-testid="resources-empty">
          <Icon as={BookOpen} boxSize="8" color={colors.textSecondary} />
          <Text color={colors.textSecondary} fontSize="sm" textAlign="center" px="6">
            {items.length !== 0 ? "Nothing matches your search."
              : !navigator.onLine ? "You're offline — the library will load when you reconnect."
              : canManage ? "No resources yet — upload your first manual, procedure or bulletin."
              : "No resources yet. Your safety manager will add documents here."}
          </Text>
        </Flex>
      ) : (
        <Stack gap="2.5">
          {filtered.map((r) => (
            <Flex key={r.id} data-testid={`resource-card-${r.id}`} align="center" gap="3"
              bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="12px" px="4" py="3">
              <Flex align="center" justify="center" w="42px" h="42px" borderRadius="10px" bg={colors.tealTint} flexShrink={0}>
                <Icon as={fileIcon(r.file_type)} boxSize="5" color={colors.teal} />
              </Flex>
              <Box flex="1" minW="0">
                <Text fontWeight="600" fontSize="sm" color={colors.textPrimary} lineClamp={1}>{r.title}</Text>
                {r.description && <Text fontSize="xs" color={colors.textSecondary} lineClamp={1}>{r.description}</Text>}
                <HStack gap="2" mt="1" flexWrap="wrap">
                  <Badge bg={colors.cream} color={colors.textSecondary} borderRadius="full" px="2" fontSize="10px">{r.category}</Badge>
                  <Badge bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="2" fontSize="10px" data-testid={`resource-version-${r.id}`}>v{r.version || 1}</Badge>
                  {r.company_id === null && (
                    <Badge bg={colors.teal} color="white" borderRadius="full" px="2" fontSize="10px" data-testid={`hakina-badge-${r.id}`}>
                      Hakina
                    </Badge>
                  )}
                  <Text fontSize="10px" color={colors.textSecondary}>{ALLOWED[r.file_type] || "File"} · {fmtSize(r.file_size)} · {new Date(r.created_at).toLocaleDateString([], { dateStyle: "medium" })}</Text>
                </HStack>
              </Box>
              <Stack gap="1.5">
                <Button size="xs" variant="outline"
                  onClick={() => view(r)} loading={opening === r.id} data-testid={`view-resource-${r.id}`}>
                  <ExternalLink size={13} /> View
                </Button>
                {canRequest && (
                  <Button size="xs" bg={colors.teal} color="white" _hover={{ bg: colors.tealHover }}
                    onClick={() => setReqAckFor(r)} data-testid={`request-ack-${r.id}`}>
                    Request
                  </Button>
                )}
                {((canManage && r.company_id !== null) || (isSuperAdmin && r.company_id === null)) && (
                  <HStack gap="1">
                    <Button size="xs" variant="ghost" color={colors.tealHover} title="Update file (new version)"
                      onClick={() => { setUpdFor(r); updRef.current?.click(); }} data-testid={`update-resource-${r.id}`}>
                      <RefreshCw size={13} />
                    </Button>
                    <Button size="xs" variant="ghost" color={colors.poppy} onClick={() => setDelTarget(r)} data-testid={`delete-resource-${r.id}`}>
                      <Trash2 size={13} />
                    </Button>
                  </HStack>
                )}
              </Stack>
            </Flex>
          ))}
        </Stack>
      )}

      {uploading && (
        <UploadModal companyId={activeCompanyId} isGlobal={isSuperAdmin} flash={flash} defaultCategory={cat} existing={items}
          onClose={() => setUploading(false)}
          onDone={(uploadedCat) => { setUploading(false); setCat(uploadedCat); load(); }} />
      )}

      {reqAckFor && <RequestAckModal resource={reqAckFor} onClose={() => setReqAckFor(null)} />}

      <input ref={updRef} type="file" hidden onChange={pickNewVersion} data-testid="new-version-file-input"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,image/jpeg,image/png,image/webp" />

      <ConfirmModal open={!!delTarget} onClose={() => setDelTarget(null)}
        title={delTarget ? `Delete “${delTarget.title}”?` : ""}
        body="Employees will no longer see this document in the library. Completed acknowledgements stay in the record."
        confirmLabel="Delete" danger busy={delBusy} onConfirm={remove} testId="resource-delete-modal" />

      <ConfirmModal open={!!updTarget} onClose={() => setUpdTarget(null)}
        title={updTarget ? `Publish “${updTarget.resource.title}” v${(updTarget.resource.version || 1) + 1}?` : ""}
        body={updTarget ? `New file: ${updTarget.file.name}.\nOpen acknowledgements of v${updTarget.resource.version || 1} will be marked Superseded (kept as history) and a fresh acknowledgement will automatically be re-requested from the same people against the new version. Completed sign-offs are preserved.` : ""}
        confirmLabel="Publish new version" busy={updBusy} onConfirm={doNewVersion} testId="new-version-modal" />
    </Box>
  );
}
